import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext.jsx';
import { useSubscription } from '../contexts/SubscriptionContext.jsx';
import { useAnonymous } from '../contexts/AnonymousContext.jsx';
import { api } from '../utils/api.js';
import { API_ENDPOINTS } from '../config/api.js';
import Header from '../components/Header.jsx';
import NBackGame from '../components/NBackGame.jsx';
import LoadingSpinner from '../components/LoadingSpinner.jsx';
import ErrorMessage from '../components/ErrorMessage.jsx';
import UsageBanner from '../components/UsageBanner.jsx';
import FreeWelcomeModal from '../components/FreeWelcomeModal.jsx';
import { getRelativeTime } from '../utils/dateUtils.js';

const GamePage = () => {
  const { user, isAuthenticated, updateEducationFlags } = useAuth();
  const { fetchStatus, tier } = useSubscription();
  const { roundsToday: anonymousRoundsToday, dailyLimit: anonymousDailyLimit, incrementRounds, setRoundsCount } = useAnonymous();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(null);
  const [dimensionLevels, setDimensionLevels] = useState({});
  const [gameState, setGameState] = useState('settings'); // 'settings', 'countdown', 'playing', 'summary'
  const [showFreeWelcome, setShowFreeWelcome] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      loadProgress();
    } else {
      // Anonymous users: skip progress loading, but fetch backend usage to sync
      setLoading(false);
      syncAnonymousUsage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Separate effect for welcome modal - triggers when tier is loaded
  useEffect(() => {
    if (isAuthenticated && tier && user?.metadata?.educationFlags) {
      const flags = user.metadata.educationFlags;
      const hasSeenFreeWelcome = flags.hasSeenFreeWelcome;
      
      // Only show free welcome modal on game page (Pro handled on payment success)
      if (tier === 'free' && !hasSeenFreeWelcome) {
        // Small delay for smooth transition
        setTimeout(() => {
          setShowFreeWelcome(true);
          // Update the flag in the backend
          updateEducationFlags({
            ...user.metadata.educationFlags,
            hasSeenFreeWelcome: true
          });
        }, 800);
      }
    }
  }, [isAuthenticated, tier, user, updateEducationFlags]);
  
  const syncAnonymousUsage = useCallback(async () => {
    try {
      const response = await api.get('/api/anonymous/status');
      const backendCount = response.usage?.current || 0;
      
      // Sync localStorage with backend count (backend is source of truth)
      if (backendCount !== anonymousRoundsToday) {
        setRoundsCount(backendCount);
      }
    } catch (error) {
      console.log('Could not sync anonymous usage:', error);
      // Not critical, continue anyway
    }
  }, [anonymousRoundsToday, setRoundsCount]);

  const loadProgress = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.get(API_ENDPOINTS.GET_PROGRESS);
      setProgress(response.progress);
      
      // Convert dimension combos to level map for game
      const levels = {};
      const combos = response.progress?.profile?.metadata?.dimensionCombos || [];
      
      combos.forEach(combo => {
        // Combo code is already in format like "PA", "PACS", etc.
        // We need to convert to the game's format: comma-separated sorted dimensions
        const dimensions = combo.comboCode.split('').map(code => {
          // Map single letter codes to dimension names (must match server/src/utils/dimensions.js)
          const mapping = {
            'P': 'position',
            'A': 'audio',
            'V': 'voice',
            'M': 'note',
            'C': 'color',
            'S': 'shape',
            'N': 'number',
            'F': 'face',
            'R': 'arrow',
            'O': 'movement',
            'T': 'pattern',
            'B': 'border',
            'L': 'orbitalPosition',
            'Z': 'circleSize',
            'X': 'circleRotation',
            'I': 'circleBrightness',
            'W': 'categoryText',
            'E': 'categoryAudio',
            'Y': 'nameText',
            'U': 'nameAudio',
            'Q': 'math',
            'H': 'rhythm',
            'D': 'mandarinTone',
            'G': 'volume'
          };
          return mapping[code] || code;
        });
        
        const key = dimensions.sort().join(',');
        levels[key] = combo.currentLevel || 1;
      });
      
      setDimensionLevels(levels);
    } catch (err) {
      console.error('Failed to load progress:', err);
      setError(err.message || 'Failed to load progress');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleRoundComplete = useCallback(async (roundData) => {
    // Anonymous users: just increment localStorage count
    if (!isAuthenticated) {
      try {
        console.log('Anonymous user: calling backend to track round...');
        // Call backend to track IP-based usage
        const response = await api.post('/api/anonymous/round', {});
        console.log('Backend response:', response);
        
        // Sync localStorage with backend count (backend is source of truth)
        const backendCount = response.usage?.current || 0;
        setRoundsCount(backendCount);
        
        return {
          success: false, // Not saved to user account
          anonymous: true,
          usage: response.usage
        };
      } catch (err) {
        console.error('Error tracking anonymous round:', err);
        console.error('Error details:', { status: err.status, data: err.data });
        
        // Check if limit reached
        if (err.status === 403) {
          return {
            success: false,
            limitReached: true,
            limitType: 'anonymous',
            error: err.data?.message || 'Trial limit reached',
            anonymous: true
          };
        }
        
        return {
          success: false,
          anonymous: true,
          error: err.message || 'Failed to count round'
        };
      }
    }
    
    // Authenticated users: save to backend
    try {
      const response = await api.post(API_ENDPOINTS.SAVE_ROUND, roundData);
      
      // Refresh subscription status to update usage stats
      // Do this in the background without blocking the UI
      fetchStatus().catch(err => console.error('Failed to refresh status:', err));
      
      return { 
        success: true,
        usage: response.usage // Backend returns updated usage info
      };
    } catch (err) {
      console.error('Failed to save round:', err);
      
      // Check if it's a limit error
      if (err.response?.status === 403) {
        const errorData = err.response?.data;
        
        // Daily limit reached
        if (errorData?.error?.includes('Daily round limit')) {
          return {
            success: false,
            limitReached: true,
            limitType: 'daily',
            error: errorData.error,
            usage: errorData
          };
        }
        
        // No passes remaining
        if (errorData?.error?.includes('No passes')) {
          return {
            success: false,
            limitReached: true,
            limitType: 'passes',
            error: errorData.error
          };
        }
        
        // Dimension not allowed
        if (errorData?.error?.includes('Dimensions not allowed')) {
          return {
            success: false,
            limitReached: true,
            limitType: 'dimensions',
            error: errorData.error
          };
        }
      }
      
      return { 
        success: false, 
        error: err.message || 'Failed to save progress' 
      };
    }
  }, [isAuthenticated, fetchStatus, setRoundsCount]);

  const handleReturnToSettings = useCallback(async () => {
    // Reload progress data when returning to settings
    console.log('Returning to settings, reloading progress...');
    
    if (isAuthenticated) {
      // Authenticated users: reload progress and subscription status
      await loadProgress();
      await fetchStatus();
    } else {
      // Anonymous users: just sync their usage
      await syncAnonymousUsage();
    }
  }, [isAuthenticated, loadProgress, fetchStatus, syncAnonymousUsage]);

  if (loading) {
    return (
      <>
        <Header />
        <div style={{
          minHeight: 'calc(100vh - 64px)',
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <LoadingSpinner message="Loading your progress..." />
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header />
        <div style={{
          minHeight: 'calc(100vh - 64px)',
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
          padding: '2rem'
        }}>
          <div style={{
            maxWidth: '600px',
            margin: '0 auto'
          }}>
            <ErrorMessage error={error} onRetry={loadProgress} />
          </div>
        </div>
      </>
    );
  }

  const combos = progress?.profile?.metadata?.dimensionCombos || [];

  return (
    <>
      {gameState === 'settings' && <Header />}
      {gameState === 'settings' && <UsageBanner />}
      <div style={{
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
        minHeight: gameState === 'settings' ? 'calc(100vh - 64px)' : '100vh',
        display: 'flex',
        justifyContent: 'center',
        padding: gameState === 'settings' ? (window.innerWidth < 768 ? '0.5rem' : '1rem') : '0',
        maxWidth: '100%',
        boxSizing: 'border-box'
      }}>
        {/* Game Component */}
        <div style={{ 
          width: '100%',
          maxWidth: '100%',
          overflow: 'hidden'
        }}>
          <NBackGame 
            initialDimensionLevels={dimensionLevels}
            onRoundComplete={handleRoundComplete}
            onReturnToSettings={handleReturnToSettings}
            trainedCombos={combos}
            onGameStateChange={setGameState}
            progressData={progress}
            isAnonymous={!isAuthenticated}
          />
        </div>
      </div>
      
      {/* Free Welcome Modal (Pro welcome shown on payment success page) */}
      <FreeWelcomeModal 
        isOpen={showFreeWelcome}
        onClose={() => setShowFreeWelcome(false)}
      />
    </>
  );
};

export default GamePage;

